import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, newKey } from "@/lib/api";
import DatePicker, { isoToDmy } from "@/components/DatePicker";
import { inr, todayISO, toPaise, TXN } from "@/lib/format";
import { toast } from "sonner";

export function AdminTxnDialog({ mode, employee, balance, open, onOpenChange, onDone }) {
  const [f, setF] = useState({ amount: "", date: todayISO(), reference: "", remarks: "", direction: "increase" });
  const [key, setKey] = useState(newKey());
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setF({ amount: "", date: todayISO(), reference: "", remarks: "", direction: "increase" }); setKey(newKey()); setStep(0); }
  }, [open]);
  const p = toPaise(f.amount);
  const isAdj = mode === "adjustment";
  const after = (balance * 100 + (isAdj && f.direction === "decrease" ? -(p || 0) : p || 0)) / 100;
  const valid = p > 0 && f.date && (!isAdj || f.remarks.trim()) && after >= 0;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/transactions", { type: mode, employee_code: employee.code, ...f, idempotency_key: key });
      toast.success(`${data.transaction.type_label} recorded. Outstanding: ${inr(data.balance_after)}`);
      onOpenChange(false); onDone();
    } catch (e) {
      toast.error(errMsg(e)); setStep(0);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle>{isAdj ? "Balance adjustment" : "Record advance issued"}</DialogTitle>
          <DialogDescription>{employee?.code} – {employee?.name} · Current outstanding {inr(balance)}</DialogDescription>
        </DialogHeader>
        {step === 0 ? (
          <div className="space-y-4">
            {isAdj && (
              <RadioGroup value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })} className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="increase" data-testid="adjust-increase" /> Increase balance</label>
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="decrease" data-testid="adjust-decrease" /> Decrease balance</label>
              </RadioGroup>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Amount (₹) *</Label><Input inputMode="decimal" className="mt-1.5 money h-11" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid={`${mode}-amount-input`} /></div>
              <div><Label>Date *</Label><DatePicker max={todayISO()} className="mt-1.5 h-11" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid={`${mode}-date-input`} /></div>
            </div>
            <div><Label>Reference</Label><Input className="mt-1.5" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid={`${mode}-reference-input`} /></div>
            <div><Label>{isAdj ? "Explanation *" : "Remarks"}</Label><Textarea rows={2} className="mt-1.5" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid={`${mode}-remarks-input`} /></div>
            <div className="rounded-lg bg-slate-50 p-3 text-sm flex justify-between"><span>Outstanding after entry</span><span className={`money font-semibold ${after < 0 ? "text-red-700" : ""}`} data-testid={`${mode}-after-balance`}>{inr(after)}</span></div>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            Confirm {TXN[mode].label.toLowerCase()} of <b>{inr((p || 0) / 100)}</b>{isAdj ? ` (${f.direction})` : ""} for {employee?.name}. New outstanding: <b>{inr(after)}</b>.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => (step ? setStep(0) : onOpenChange(false))}>{step ? "Back" : "Cancel"}</Button>
          {step === 0 ? (
            <Button className="bg-blue-800 hover:bg-blue-900" disabled={!valid} onClick={() => setStep(1)} data-testid={`${mode}-continue-btn`}>Continue</Button>
          ) : (
            <Button className="bg-blue-800 hover:bg-blue-900" disabled={busy} onClick={save} data-testid={`${mode}-confirm-btn`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm & save</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReverseDialog({ txn, onOpenChange, onDone }) {
  const [reason, setReason] = useState("");
  const [key, setKey] = useState(newKey());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setReason(""); setKey(newKey());
  }, [txn]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/transactions/${txn.id}/reverse`, { remarks: reason, idempotency_key: key });
      toast.success(`Reversal recorded. Outstanding: ${inr(data.balance_after)}`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!txn} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reverse-dialog">
        <DialogHeader>
          <DialogTitle>Reverse transaction</DialogTitle>
          <DialogDescription>The original entry stays in the ledger. An opposite Reversal/Correction entry will be added.</DialogDescription>
        </DialogHeader>
        {txn && <div className="rounded-lg bg-slate-50 p-3 text-sm">{txn.type_label} · {isoToDmy(txn.date)} · Dr {inr(txn.debit)} · Cr {inr(txn.credit)}<div className="text-xs text-slate-500">{txn.reference}</div></div>}
        <div><Label>Reason for reversal *</Label><Textarea rows={2} className="mt-1.5" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reverse-reason-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-red-700 hover:bg-red-800" disabled={busy || !reason.trim()} onClick={save} data-testid="reverse-confirm-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Reverse entry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
